import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import { DisablerAction, type DisablerEvent, DisablerStatus, type Vehicle } from '@cobliteam/common-types';

import { DisablerRequester } from '@cobliteam/api-lib';
import GrahpQLVehicleRequester from '@cobliteam/api-lib/src/dashboard/vehicle/GrahpQLVehicleRequester';

import { Environment } from '@cobliteam/react-environments';

import Alert from '@cobliteam/ui/components/Alert';

import { QUERY_KEY as DEVICES_QUERY_KEY } from '../useDevices';

interface ImmobileActionProps {
  deviceId: string;
  disablerAction: DisablerAction;
}

const vehicleGraphQLRequester = new GrahpQLVehicleRequester();

export const QUERY_KEY = 'VEHICLE';

const env = Environment.getInstance().getEnvironment();
const disablerRequester = new DisablerRequester({ host: env.herbieApiHost });

interface UseVehicle {
  withOdometer?: boolean;
  withDisablerStatus?: boolean;
}

const useVehicle = ({ withOdometer = false, withDisablerStatus = true }: UseVehicle = {}) => {
  const getVehicles = ({ enabled = true, refetchOnMount = false, ...options }: Partial<UseQueryOptions<Vehicle[]>>) => {
    const queryKey = [QUERY_KEY, withOdometer && 'ODOMETER', withDisablerStatus && 'DISABLER_STATUS'].filter(Boolean);

    return useQuery<Vehicle[]>({
      ...options,
      queryKey,
      queryFn: () => vehicleGraphQLRequester.getVehicles(withOdometer, withDisablerStatus),
      enabled,
      refetchOnMount,
    });
  };

  return {
    getVehicles,
  };
};

export const getVehicle = ({
  vehicleId,
  options,
}: {
  vehicleId: string;
  options?: UseQueryOptions<Vehicle, 'queryKey'>;
}) => {
  return useQuery<Vehicle, 'queryKey'>({
    queryKey: [QUERY_KEY, vehicleId],
    queryFn: () => vehicleGraphQLRequester.getVehicle(vehicleId),
    ...options,
  });
};

const getRequesterMethod = (action: DisablerAction) => {
  if (action === DisablerAction.Block || action === DisablerAction.CancelUnblock) {
    return disablerRequester.requestDisable;
  }
  return disablerRequester.requestEnable;
};

const immobilizerAction = async ({ deviceId, disablerAction }: ImmobileActionProps) => {
  const requester = getRequesterMethod(disablerAction);
  await requester(deviceId);
};

export const useImmobilizerAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: immobilizerAction,
    onSuccess: (_, { deviceId, disablerAction }) => {
      queryClient.setQueryData<Vehicle[]>([QUERY_KEY], (vehicles) => {
        if (!vehicles) return vehicles;

        const vehicleIndex = vehicles.findIndex((vehicle) => vehicle.deviceId === deviceId);

        if (vehicleIndex !== -1) {
          const foundedVehicle = vehicles[vehicleIndex];
          if (foundedVehicle.disablerStatus) {
            const updateVehicle = {
              ...foundedVehicle,
              disablerStatus: {
                ...foundedVehicle.disablerStatus,
                lastDisablingActionTime: Date.now(),
                status: disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock ? DisablerStatus.DisablePending : DisablerStatus.EnablePending,
              },
            };
            return [...vehicles.slice(0, vehicleIndex), updateVehicle, ...vehicles.slice(vehicleIndex + 1)];
          }
        }

        return vehicles;
      });

      queryClient.setQueryData<DisablerEvent[]>([DEVICES_QUERY_KEY, 'FLEET_STATUS'], (events) => {
        if (!events) return events;

        const eventIndex = events.findIndex((e) => e.deviceId === deviceId);

        if (eventIndex !== -1) {
          const foundedEvent = events[eventIndex];

          const updateEvent = {
            ...foundedEvent,
            lastDisablingActionTime: Date.now(),
            status: disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock ? DisablerStatus.DisablePending : DisablerStatus.EnablePending,
          };

          return [...events.slice(0, eventIndex), updateEvent, ...events.slice(eventIndex + 1)];
        }

        return events;
      });

      Alert.warning(
        disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock
          ? 'O bloqueio deste veículo já está em andamento!'
          : 'O desbloqueio deste veículo já está em andamento!'
      );
    },
    onError: (_, { disablerAction }) => {
      Alert.error(
        disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock
          ? 'Ops! O sistema apresentou um erro e não foi possível iniciar o bloqueio, tente novamente.'
          : 'Ops! O sistema apresentou um erro e não foi possível iniciar o desbloqueio, tente novamente.'
      );
    },
  });
};

export default useVehicle;
